import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SERVICES } from '../lib/services-data';
import { formatPrice } from '../lib/utils';
import { SEOHead } from '../components/SEOHead';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } }
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const categories = [
  { id: 'all', label: 'Tous' },
  { id: 'domicile', label: 'Domicile' },
  { id: 'jardin', label: 'Jardin' },
  { id: 'global', label: 'Global Service' },
];

export default function ServicesPage() {
  const [filter, setFilter] = React.useState('all');
  const filtered = filter === 'all' ? SERVICES : SERVICES.filter(s => s.category === filter);

  return (
    <div>
      <SEOHead
        title="Services à la personne : ménage, jardinage"
        description="Découvrez tous nos services à domicile : entretien ménager, jardinage, repassage, petit bricolage, aide aux courses, aide aux repas. Tous éligibles au crédit d'impôt 50%. Interventions ponctuelles ou régulières dans l'Artois."
        path="/services"
      />
      {/* Hero */}
      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Nos services</Badge>
          <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight mb-3">Tous nos services à la personne</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">Découvrez notre gamme complète de services pour faciliter votre quotidien. Tous éligibles au crédit d'impôt 50%.</p>
          
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map(cat => (
              <Button key={cat.id} variant={filter === cat.id ? 'default' : 'outline'} size="sm" onClick={() => setFilter(cat.id)}>
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate="visible" variants={stagger} data-testid="services-grid">
            {filtered.map((service) => (
              <motion.div key={service.id} variants={fadeUp}>
                <Link to={`/services/${service.id}`}>
                  <Card className="p-6 h-full bg-card border border-border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group" data-testid="service-card">
                    {service.isGlobal && (
                      <Badge className="mb-3 bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]">Global Service</Badge>
                    )}
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center mb-3">
                      <service.icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                    </div>
                    <h3 className="font-['Space_Grotesk'] font-semibold mb-1 group-hover:text-[hsl(var(--primary))] transition-colors">{service.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{service.shortDesc}</p>
                    <div className="flex items-center justify-between mt-auto">
                      <div>
                        <span className="text-xs text-muted-foreground">À partir de </span>
                        <span className="text-sm font-bold text-[hsl(var(--primary))]">{formatPrice(service.priceAfter)}/h</span>
                        <span className="text-xs text-muted-foreground ml-1">*</span>
                      </div>
                      <span className="text-xs text-muted-foreground flex items-center gap-1 group-hover:text-[hsl(var(--primary))]">
                        Détails <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
          <p className="text-xs text-muted-foreground text-center mt-4">* Tarifs après avance immédiate du crédit d'impôt 50%</p>

          {/* CTA */}
          <div className="text-center mt-12 p-8 rounded-2xl bg-[hsl(var(--brand-mint))]/30">
            <h3 className="font-['Space_Grotesk'] font-semibold text-lg mb-2">Crédit d'impôt 50% sur tous nos services</h3>
            <p className="text-sm text-muted-foreground mb-4">Grâce à l'avance immédiate, vous ne payez que le reste à charge.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/devis"><Button className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">Demander un devis <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/credit-impot"><Button variant="outline">En savoir plus</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
